import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2, History as HistoryIcon, Cpu, Upload } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import CsvImportDialog from "../components/CsvImportDialog";
import { usePermissions } from "../hooks/usePermissions";
import { useAuth } from "../context/AuthContext";

const TANK_DIMENSIONS = ["Rectangular", "Cylindrical", "Square", "Custom"];

const DEVICE_CATEGORIES = [
  { v: "pressure_sensor",  label: "Pressure",               group: "Sensor" },
  { v: "ultrasonic",       label: "Liquid Level",            group: "Sensor" },
  { v: "pump",             label: "Pump",                    group: "Mechanical" },
  { v: "dp_switch",        label: "Diff. Pressure Switch",   group: "Sensor" },
  { v: "voltage_sensor",   label: "Voltage / Power Sensor",  group: "Electrical" },
  { v: "smoke_detector",   label: "Smoke Detector",          group: "Detection" },
  { v: "heat_detector",    label: "Heat Detector",           group: "Detection" },
  { v: "mcp",              label: "Manual Call Point (MCP)", group: "Detection" },
  { v: "valve",            label: "Valve Actuator",          group: "Mechanical" },
  { v: "gateway",          label: "Gateway / IoT Controller",group: "Control" },
  { v: "other",            label: "Other",                   group: "Other" },
];

// Device-type sub-categories shown inside each category
const DEVICE_SUBTYPES = {
  pressure_sensor: ["Hydrant Riser", "Sprinkler Riser", "Booster Discharge", "Jockey Header", "Zone Valve Outlet", "Other"],
  ultrasonic:      ["Underground Tank", "Overhead Tank", "Break Tank", "Day Tank", "Diesel Day Tank", "Other"],
  pump:            ["Main Hydrant Pump", "Standby Diesel Pump", "Jockey Pump", "Sprinkler Main Pump", "Booster Pump", "Other"],
};

// Status comes from the device's gateway (see backend/device_status.py), not a stored value.
const STATUS_STYLE = {
  online: "bg-emerald-50 border border-emerald-200 text-emerald-700",
  offline: "bg-red-50 border border-red-200 text-red-700",
  fault: "bg-amber-50 border border-amber-200 text-amber-700",
  not_linked: "bg-gray-100 border border-gray-200 text-gray-500",
};
function StatusBadge({ status, className = "" }) {
  return (
    <Badge className={`border-0 ${className} ${STATUS_STYLE[status] || STATUS_STYLE.not_linked}`}
      title={status === "not_linked" ? "Not linked to a gateway, so there is no live data to check" : undefined}>
      {status === "not_linked" ? "not linked" : status}
    </Badge>
  );
}

function FieldGroup({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-gray-500">{label}</Label>
      {children}
    </div>
  );
}

// Liquid Level sensor fields
function LiquidLevelFields({ cfg, set }) {
  return (
    <>
      <FieldGroup label="Tank Capacity (Litres) *">
        <Input type="number" className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 50000" value={cfg.tank_capacity_ltr ?? ""} onChange={(e) => set("tank_capacity_ltr", e.target.value)} />
      </FieldGroup>
      <FieldGroup label="Tank Height (Metres) *">
        <Input type="number" step="0.01" className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 3.5" value={cfg.tank_height_m ?? ""} onChange={(e) => set("tank_height_m", e.target.value)} />
      </FieldGroup>
      <FieldGroup label="Tank Dimension">
        <Select value={cfg.tank_dimension || ""} onValueChange={(v) => set("tank_dimension", v)}>
          <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue placeholder="Select Tank Dimension" /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900">
            {TANK_DIMENSIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      </FieldGroup>
      <FieldGroup label="Liquid Lower Limit (%) *">
        <Input type="number" className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 10" value={cfg.liquid_lower_limit ?? ""} onChange={(e) => set("liquid_lower_limit", e.target.value)} />
      </FieldGroup>
      <FieldGroup label="Liquid Upper Limit (%) *">
        <Input type="number" className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 90" value={cfg.liquid_upper_limit ?? ""} onChange={(e) => set("liquid_upper_limit", e.target.value)} />
      </FieldGroup>
      <FieldGroup label="Minimum Liquid Level (m) *">
        <Input type="number" step="0.01" className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 0.3" value={cfg.min_liquid_level ?? ""} onChange={(e) => set("min_liquid_level", e.target.value)} />
      </FieldGroup>
      <FieldGroup label="Maximum Liquid Level (m) *">
        <Input type="number" step="0.01" className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. 3.2" value={cfg.max_liquid_level ?? ""} onChange={(e) => set("max_liquid_level", e.target.value)} />
      </FieldGroup>
    </>
  );
}


const DEVICE_TEMPLATE = [
  "device_name,device_type,site_id,zone,floor_number,make,model,low,high",
  "Hydrant Pressure Sensor A,pressure_sensor,site_xxx,Zone-1,1,Honeywell,SPS-3000,3.5,9",
  "UG Tank Level,ultrasonic,site_xxx,Zone-B1,B1,VEGAPULS,C21,,",
  "Smoke Detector 10,smoke_detector,site_xxx,Zone-1,1,Siemens,FDO241,,",
  "Main Sprinkler Pump,sprinkler_main_pump,site_xxx,Zone-P,B1,Kirloskar,KOSI-4D,,",
];


export default function DeviceManagement() {
  const { sites, selectedSite } = useSiteContext();
  const { can } = usePermissions();
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("all");
  const [devices, setDevices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ device_type: "pressure_sensor", config: {} });
  const [drawerDev, setDrawerDev] = useState(null);
  const [drawerHist, setDrawerHist] = useState(null);
  const [drawerAlerts, setDrawerAlerts] = useState([]);
  const [csvOpen, setCsvOpen] = useState(false);
  const [formGateways, setFormGateways] = useState([]);
  const [drawerGateways, setDrawerGateways] = useState([]);
  const gatewaysOf = (siteId) => (siteId
    ? api.get("/hardware/gateways", { params: { site_id: siteId } }).then((r) => r.data || []).catch(() => [])
    : Promise.resolve([]));
  useEffect(() => { gatewaysOf(form.site_id).then(setFormGateways); }, [form.site_id]);
  useEffect(() => { gatewaysOf(drawerDev?.site_id).then(setDrawerGateways); }, [drawerDev?.site_id]);

  // Linking a device to its gateway is what gives it a real online/offline status.
  const linkGateway = async (gateway_id) => {
    try {
      const r = await api.patch(`/devices/${drawerDev.device_id}`, { gateway_id });
      setDrawerDev(r.data);
      toast.success(gateway_id ? "Linked to gateway" : "Unlinked");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not link gateway"); }
  };

  // Load history + alerts when drawer opens
  React.useEffect(() => {
    if (!drawerDev) return;
    api.get(`/devices/${drawerDev.device_id}/history`, { params: { hours: 720 } })
      .then((r) => setDrawerHist(r.data)).catch(() => setDrawerHist(null));
    api.get("/alerts", { params: { site_id: drawerDev.site_id, limit: 5 } })
      .then((r) => setDrawerAlerts(r.data)).catch(() => setDrawerAlerts([]));
  }, [drawerDev]);

  const load = () => {
    const params = tab === "all" ? {} : { device_type: tab };
    if (selectedSite && selectedSite !== "all") params.site_id = selectedSite;
    api.get("/devices", { params }).then((r) => setDevices(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, selectedSite]);

  const save = async () => {
    try {
      if (!form.device_name || !form.site_id) { toast.error("Name and site required"); return; }
      await api.post("/devices", form);
      toast.success("Device added");
      setOpen(false); setForm({ device_type: "pressure_sensor", config: {} }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete device?")) return;
    await api.delete(`/devices/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div className="space-y-6" data-testid="devices-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold">Device Management</h1>
          <p className="text-sm text-gray-500 mt-1">Sensors, pumps, panels, gateways & valves</p>
          <div className="text-[11px] text-amber-600 mt-1 max-w-2xl">
            Legacy device &amp; telemetry browser. For structured per-system assets (pumps, tanks, valves)
            with compliance tracking and live data linking to gateway registers, use the Asset Registries instead.
          </div>
        </div>
        <div className="flex gap-2">
        {can("device_management", "create") && (
          <Button onClick={() => setOpen(true)} className="f360-gradient border-0" data-testid="add-device-btn">
            <Plus className="w-4 h-4 mr-1" strokeWidth={2.5} /> Add Device
          </Button>
        )}
        {can("device_management", "create") && (
        <Button onClick={() => setCsvOpen(true)} variant="outline" className="border-gray-200" data-testid="import-devices-btn">
          <Upload className="w-4 h-4 mr-1" strokeWidth={2.5} /> Import CSV
        </Button>
        )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-gray-50 border border-gray-200">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="pump">Pumps</TabsTrigger>
          <TabsTrigger value="pressure_sensor">Sensors</TabsTrigger>
          <TabsTrigger value="fire_panel">Panels</TabsTrigger>
          <TabsTrigger value="gateway">Gateways</TabsTrigger>
          <TabsTrigger value="valve">Valves</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <div className="glass rounded-lg overflow-hidden">
            <div className="table-scroll">
            <Table>
              <TableHeader>
                <TableRow className="border-gray-200 hover:bg-transparent">
                  <TableHead>Device ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead>Zone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {devices.map((d) => (
                  <TableRow key={d.device_id} className="border-gray-200 cursor-pointer hover:bg-gray-50" onClick={() => setDrawerDev(d)}>
                    <TableCell className="font-mono text-xs text-orange-700">{d.device_id}</TableCell>
                    <TableCell className="font-semibold">{d.device_name}</TableCell>
                    <TableCell className="text-xs">{d.device_type}</TableCell>
                    <TableCell className="text-xs">{sites.find((s) => s.site_id === d.site_id)?.name || d.site_id}</TableCell>
                    <TableCell>{d.zone || "—"}</TableCell>
                    <TableCell><StatusBadge status={d.status} /></TableCell>
                    <TableCell className="text-xs text-gray-500">{d.last_seen ? new Date(d.last_seen).toLocaleTimeString("en-IN") : "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => nav(`/devices/${d.device_id}/history`)} className="text-orange-700" data-testid={`hist-${d.device_id}`}><HistoryIcon className="w-4 h-4" /></Button>
                      {can("device_management", "delete") && (
                      <Button variant="ghost" size="sm" onClick={() => del(d.device_id)} className="text-red-400"><Trash2 className="w-4 h-4" strokeWidth={2.5} /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!devices.length && <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">No devices.</TableCell></TableRow>}
              </TableBody>
            </Table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="device-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Add Device</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1">
            {/* ROW 1 — Location + Device Category */}
            <FieldGroup label="Location (Site) *">
              <Select value={form.site_id || ""} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue placeholder="Select Location" /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200 text-gray-900">
                  {sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </FieldGroup>
            <FieldGroup label="Device Category *">
              <Select value={form.device_type} onValueChange={(v) => setForm({ ...form, device_type: v, config: {} })}>
                <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue placeholder="Select Category" /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200 text-gray-900">
                  {DEVICE_CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </FieldGroup>

            {/* ROW 2 — Serial No + Description */}
            <FieldGroup label="Device Serial No. *">
              <Input className="bg-gray-50 border-gray-200 mt-1" placeholder="Device Serial No." value={form.serial_number || ""} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} />
            </FieldGroup>
            <FieldGroup label="Device Description *">
              <Input className="bg-gray-50 border-gray-200 mt-1" placeholder="Device Description" value={form.device_name || ""} onChange={(e) => setForm({ ...form, device_name: e.target.value })} />
            </FieldGroup>

            {/* ROW 3 — Zone + Floor */}
            <FieldGroup label="Zone">
              <Input className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. Zone-1" value={form.zone || ""} onChange={(e) => setForm({ ...form, zone: e.target.value })} />
            </FieldGroup>
            <FieldGroup label="Floor">
              <Input className="bg-gray-50 border-gray-200 mt-1" placeholder="e.g. B1" value={form.floor_number || ""} onChange={(e) => setForm({ ...form, floor_number: e.target.value })} />
            </FieldGroup>
            <FieldGroup label="Gateway (for live status)">
              <Select value={form.gateway_id || "none"} onValueChange={(v) => setForm({ ...form, gateway_id: v === "none" ? "" : v })}>
                <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200 text-gray-900">
                  <SelectItem value="none">Not linked</SelectItem>
                  {formGateways.map((g) => <SelectItem key={g.gateway_id} value={g.gateway_id}>{g.gateway_name || g.gateway_id}</SelectItem>)}
                </SelectContent>
              </Select>
            </FieldGroup>

            {/* ROW 4 — Device sub-type (if applicable) */}
            {DEVICE_SUBTYPES[form.device_type] && (
              <FieldGroup label="Device Type *">
                <Select value={form.config?.sub_type || ""} onValueChange={(v) => setForm({ ...form, config: { ...form.config, sub_type: v } })}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue placeholder="Select Device Type" /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 text-gray-900">
                    {DEVICE_SUBTYPES[form.device_type].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </FieldGroup>
            )}

            {/* SEPARATOR */}
            {form.device_type === "ultrasonic" && (
              <div className="md:col-span-2 border-t border-gray-200 pt-1">
                <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">Level Transmitter Configuration</div>
              </div>
            )}

            {/* CATEGORY-SPECIFIC FIELDS */}
            {form.device_type === "ultrasonic" && (
              <LiquidLevelFields
                cfg={form.config || {}}
                set={(k, v) => setForm((f) => ({ ...f, config: { ...(f.config || {}), [k]: v } }))}
              />
            )}
          </div>

          <DialogFooter className="mt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Close</Button>
            <Button onClick={save} className="f360-gradient border-0" data-testid="save-device-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Device drill-down drawer */}
      <Sheet open={!!drawerDev} onOpenChange={(o) => !o && setDrawerDev(null)}>
        <SheetContent side="right" className="bg-white border-gray-200 text-gray-900 w-full sm:max-w-[480px] overflow-y-auto">
          {drawerDev && (
            <>
              <SheetHeader className="border-b border-gray-200 pb-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-mono text-xs text-orange-700">{drawerDev.device_id}</span>
                  <Badge variant="outline" className="border-gray-200 text-xs">{drawerDev.device_type}</Badge>
                  <StatusBadge status={drawerDev.status} className="text-xs" />
                </div>
                <SheetTitle className="text-gray-900 text-2xl">{drawerDev.device_name}</SheetTitle>
                <div className="text-xs text-gray-500">{sites.find((s) => s.site_id === drawerDev.site_id)?.name}</div>
              </SheetHeader>

              <div className="py-4 space-y-5">
                <section>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 font-bold">Device Info</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-50 rounded-lg p-3"><div className="text-gray-500">Make / Model</div><div className="font-bold mt-0.5">{drawerDev.config?.type || "—"}</div></div>
                    <div className="bg-gray-50 rounded-lg p-3"><div className="text-gray-500">Zone / Floor</div><div className="font-bold mt-0.5">{drawerDev.zone || "—"} · {drawerDev.floor_number || "—"}</div></div>
                    <div className="bg-gray-50 rounded-lg p-3"><div className="text-gray-500">Low Threshold</div><div className="font-mono mt-0.5">{drawerDev.config?.low ?? "—"} {drawerHist?.unit}</div></div>
                    <div className="bg-gray-50 rounded-lg p-3"><div className="text-gray-500">High Threshold</div><div className="font-mono mt-0.5">{drawerDev.config?.high ?? "—"} {drawerHist?.unit}</div></div>
                    <div className="bg-gray-50 rounded-lg p-3 col-span-2"><div className="text-gray-500">Last Seen</div><div className="text-emerald-400 font-bold mt-0.5">{drawerDev.last_seen ? new Date(drawerDev.last_seen).toLocaleString("en-IN") : "—"}</div></div>
                    <div className="bg-gray-50 rounded-lg p-3 col-span-2">
                      <div className="text-gray-500 mb-1">Gateway (status comes from its live data)</div>
                      <Select value={drawerDev.gateway_id || "none"} onValueChange={(v) => linkGateway(v === "none" ? "" : v)} disabled={user?.role !== "super_admin"}>
                        <SelectTrigger className="bg-white border-gray-200 h-8 text-xs" data-testid="device-gateway-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-gray-200 text-gray-900">
                          <SelectItem value="none">Not linked</SelectItem>
                          {drawerGateways.map((g) => <SelectItem key={g.gateway_id} value={g.gateway_id}>{g.gateway_name || g.gateway_id}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </section>

                <section>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 font-bold">Telemetry — last 30 days</div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    {drawerHist?.readings?.length ? (
                      <ResponsiveContainer width="100%" height={180}>
                        <LineChart data={drawerHist.readings.map((r) => ({ ts: new Date(r.recorded_at).toLocaleDateString([], { month: "short", day: "numeric" }), value: r.value }))}>
                          <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
                          <XAxis dataKey="ts" stroke="#64748b" fontSize={9} />
                          <YAxis stroke="#64748b" fontSize={9} />
                          <Tooltip contentStyle={{ background: "#0a0f1e", border: "1px solid #333" }} />
                          {drawerHist.thresholds?.warn && <ReferenceLine y={drawerHist.thresholds.warn} stroke="#f59e0b" strokeDasharray="4 4" />}
                          {drawerHist.thresholds?.critical && <ReferenceLine y={drawerHist.thresholds.critical} stroke="#ef4444" strokeDasharray="4 4" />}
                          <Line type="monotone" dataKey="value" stroke="#ff6b35" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : <div className="text-gray-500 text-center py-8 text-xs">No readings.</div>}
                  </div>
                  <Button onClick={() => { nav(`/devices/${drawerDev.device_id}/history`); setDrawerDev(null); }} className="w-full mt-2 bg-gray-100 hover:bg-white/15 text-xs">View Full History →</Button>
                </section>

                <section>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 font-bold">Recent Site Alerts</div>
                  <div className="space-y-1.5">
                    {drawerAlerts.slice(0, 5).map((a) => (
                      <div key={a.alert_id} className="bg-gray-50 rounded-lg p-2.5">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`border-0 text-[10px] ${a.severity === "critical" || a.severity === "high" ? "bg-red-50 border border-red-200 text-red-700" : a.severity === "medium" ? "bg-amber-50 border border-amber-200 text-amber-700" : "bg-slate-100 border border-slate-200 text-slate-700"}`}>{a.severity}</Badge>
                          <span className="text-xs font-bold truncate flex-1">{a.title}</span>
                        </div>
                        <div className="text-[10px] text-gray-500">{new Date(a.created_at).toLocaleString("en-IN")} · {a.status || (a.acknowledged ? "ack" : "open")}</div>
                      </div>
                    ))}
                    {!drawerAlerts.length && <div className="text-gray-500 text-xs text-center py-3">No alerts.</div>}
                  </div>
                </section>

                {can("device_management", "delete") && (
                  <Button onClick={() => { if (window.confirm("Delete this device?")) { del(drawerDev.device_id); setDrawerDev(null); } }} className="w-full bg-red-500/20 hover:bg-red-500/30 text-red-700"><Trash2 className="w-4 h-4 mr-2" strokeWidth={2.5} />Delete Device</Button>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <CsvImportDialog
        open={csvOpen}
        onOpenChange={setCsvOpen}
        title="Import Devices from CSV"
        endpoint="/devices/bulk-import"
        templateRows={DEVICE_TEMPLATE}
        templateFilename="devices_template.csv"
        onDone={load}
      />
    </div>
  );
}
